import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { SelectItem } from '@openng/optimus-ui/api';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { GeneralGridComponent } from 'src/app/core/components/general-grid.component';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { CommercialOutageSearch } from 'src/app/model/commercial-services/commercial-outage';
import { COMMERCIAL_OUTAGE_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import { NEW_COMMERCIAL_OUTAGE } from 'src/app/constants/commercial-constants';
import { TabService } from 'src/app/core/services/tab.service';
import { AorApiService } from 'src/app/services/api/aor-api.service';
import { CommercialOutageApiService } from 'src/app/services/api/commercial-outage-api.service';
import { BaseTableService } from 'src/app/services/table/base-table.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { getCommercialOutageTableColumns } from '../../definitions/commercial-outages-table-definition';
import { CommercialFacilityApiService } from 'src/app/services/api/commercial-facility-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CLAIMS } from 'src/app/constants/auth-constants';

@Component({
  selector: 'app-commercial-outages-search-container',
  templateUrl: './commercial-outages-search-container.component.html',
  styleUrls: ['./commercial-outages-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BlockUI,
    ButtonDirective,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class CommercialOutagesSearchContainerComponent
  extends SearchContainerComponent<CommercialOutageSearch>
  implements OnInit, OnDestroy
{
  protected _translateService = inject(TranslateService);
  protected _commercialOutageApi = inject(CommercialOutageApiService);
  protected _commercialFacilityApi = inject(CommercialFacilityApiService);
  protected _aorApi = inject(AorApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _authApi = inject(AuthApiService);

  readonly canAddCommercialOutage = signal(
    this._authApi.doesUserHaveClaim(
      CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_OUTAGES.CREATE_COMMERCIAL_OUTAGES
    )
  );

  availableAors: SelectItem[] = [];
  availableOperatingAreas: SelectItem[] = [];
  availableMarketAreas: SelectItem[] = [];
  scrollHeight = 'calc(100vh - 256px)';
  activeTabChangedSub?: Subscription;

  ngOnDestroy(): void {
    this.activeTabChangedSub?.unsubscribe();
  }

  ngOnInit(): void {
    this.setTableService(new BaseTableService<CommercialOutageSearch>());

    const label = this._translateService.instant(
      'COMMERCIAL_SERVICES.SCREEN.COMMERCIAL_OUTAGES'
    );
    this.screenName = label;

    this._tabService.updateActiveTabLabel(label);

    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (tab?.header === this.screenName) {
          this.refresh();
        }
      }
    );

    forkJoin({
      availableAors: this._aorApi.getAORs(null),
      availableOperatingAreas:
        this._commercialFacilityApi.getCommercialOperatingAreas(),
      availableMarketAreas:
        this._commercialFacilityApi.getCommercialMarketAreas(),
    }).subscribe({
      next: ({
        availableAors,
        availableOperatingAreas,
        availableMarketAreas,
      }) => {
        this.availableAors = [
          ...availableAors.map((item) => ({
            label: item.name,
            value: item.id,
          })),
        ];
        this.availableOperatingAreas = [
          ...availableOperatingAreas.map((item) => ({
            label: item.name,
            value: item.id,
          })),
        ];
        this.availableMarketAreas = [
          ...availableMarketAreas.map((item) => ({
            label: item.name,
            value: item.id,
          })),
        ];
        this.columns = getCommercialOutageTableColumns(
          this.availableAors,
          this.availableOperatingAreas,
          this.availableMarketAreas
        );
        this.applyPreviousFilters();
        this.applyPreviousData();
      },
      error: (error) => {
        console.error(error);
        this.loading = false;
      },
    });
  }

  changeViewEvent($event: { data: CommercialOutageSearch }): void {
    const tab = this._tabService.buildNewTab(
      COMMERCIAL_OUTAGE_DETAIL_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  addNewCommercialOutage(): void {
    const tab = this._tabService.buildNewTab(
      COMMERCIAL_OUTAGE_DETAIL_CONTAINER,
      true,
      undefined,
      NEW_COMMERCIAL_OUTAGE
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  override handleLazyLoad(
    request: PageRequest,
    event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ): void {
    request.page += 1;
    const query = this.buildQuery(filters);
    const sort = this.buildNetworkSortQuery(event, 'outageStartDate', 'DESC');

    this._commercialOutageApi
      .queryForCommercialOutages(request.pageSize, request.page, query, sort)
      .subscribe({
        next: (result) => {
          this.elements = result.items;
          this.finalizeQuery(result.totalRecords);
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey:
              'COMMERCIAL_SERVICES.MESSAGES.ERROR.LOAD_COMMERCIAL_OUTAGES',
          });
          this.loading = false;
        },
      });
  }

  refresh(): void {
    const lazy = this.tableService.getLastLazyLoad();
    this.lazyLoad({
      lazy: {
        ...lazy,
        first: lazy?.first ?? 0,
        rows: lazy?.rows ?? this.rows,
      },
    });
  }

  private buildQuery(filters: LazyLoadFilters): Record<string, string> {
    const query: Record<string, string> = {};
    for (const [field, metadata] of Object.entries(filters)) {
      const filter = Array.isArray(metadata) ? metadata[0] : metadata;
      const value = filter?.value;
      if (value === null || value === undefined || value === '') {
        continue;
      }
      if (value instanceof Date) {
        query[field] = value.toISOString();
      } else {
        query[field] = String(value).trim();
      }
    }
    return query;
  }
}
